'use client';

import { useRef, useEffect, useState } from 'react';
import { AnimationContainer } from '@/components/ui/animation-container';
import { MagicBadge } from '@/components/ui/magic-badge';
import { Button } from '@/components/ui/button';
import Link from '@/components/i18n/LocaleLink';
import { CheckCircle, Coins, CreditCard } from 'lucide-react';
import { TranslationKeys, Plan } from '../types';

/**
 * Decide whether a plan belongs to the "For teams" audience.
 * Matches by slug ('team', 'business', 'enterprise') or by name
 * containing the words, so it works for both the DB-backed plans
 * and the fallback static list.
 */
function isTeamPlan(plan: Plan): boolean {
  const slug = (plan.slug || '').toLowerCase();
  const name = (plan.name || '').toLowerCase();
  const teamKeywords = ['team', 'business', 'enterprise'];
  return teamKeywords.some((k) => slug.includes(k) || name.includes(k));
}

/** Lightweight viewport-triggered fade-in card (replaces framer-motion) */
function FadeInCard({ index, className, children }: { index: number; className?: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1, rootMargin: '50px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
        transition: `opacity 0.5s ease-out ${index * 0.15}s, transform 0.5s ease-out ${index * 0.15}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function PricingSection({
  t,
  plans,
  isYearly,
  setIsYearly,
  planStartIndex,
  setPlanStartIndex,
  copy,
}: {
  t: TranslationKeys;
  plans: Plan[];
  isYearly: boolean;
  setIsYearly: (v: boolean) => void;
  planStartIndex: number;
  setPlanStartIndex: (v: number) => void;
  copy?: import('@/lib/marketing/site-copy').SiteCopy;
}) {
  const { copyOr } = require('@/lib/marketing/site-copy') as typeof import('@/lib/marketing/site-copy');
  const title = copyOr(copy, 'home.pricing.title', t.pricingTitle);
  const subtitle = copyOr(copy, 'home.pricing.subtitle', t.pricingDesc);
  // Audience toggle: "For individuals" shows personal plans, "For teams"
  // shows team/business plans. Replaces the old prev/next chevrons that
  // made the Team plan hard to discover.
  const [audience, setAudience] = useState<'individuals' | 'teams'>('individuals');
  return (
    <section id="pricing" className="py-6 px-4 bg-gradient-to-b from-white via-purple-50/50 to-white">
      <div className="max-w-7xl mx-auto">
        <AnimationContainer delay={0.1}>
          <div className="flex flex-col items-center justify-center w-full py-2 max-w-xl mx-auto">
            <MagicBadge title={t.pricing} />
            <h2 className="text-center text-3xl md:text-5xl !leading-[1.1] font-bold text-gray-900 mt-6">
              {title}
            </h2>
            <p className="mt-4 text-center text-lg text-gray-600 max-w-lg">
              {subtitle}
            </p>

            {/* Audience toggle: Individuals | Teams */}
            <div className="flex items-center gap-1 mt-8 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setAudience('individuals')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                  audience === 'individuals'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.pricingToggleIndividuals}
              </button>
              <button
                onClick={() => setAudience('teams')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                  audience === 'teams'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.pricingToggleTeams}
              </button>
            </div>

            {/* Monthly/Yearly Toggle */}
            <div className="flex items-center gap-1 mt-4 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setIsYearly(false)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                  !isYearly
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.monthly}
              </button>
              <button
                onClick={() => setIsYearly(true)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                  isYearly
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.yearly}
              </button>
            </div>
          </div>
        </AnimationContainer>

        {(() => {
            // Fallback reflects the 5 real plans seeded in DB: Free, Pro, Max, Team, Enterprise.
            const allPlans = plans.length > 0 ? plans : [
              { id: 1, slug: 'free', name: 'Free', description: 'Prueba EZDocuAI', monthlyPrice: '0', yearlyPrice: '0', yearlyDiscount: 0, benefits: ['5 páginas gratis/mes. Se reinicia cada ciclo.', 'Adicionales: $2.99/página.', 'OCR de alta precisión', 'Preservación de formato', 'Traducción con IA', 'Editor doble panel sincronizado', 'Export PDF y DOCX', 'Retención fija: 7 días'], creditsMonthly: 5, creditsYearly: 5, pagesPerMonth: 5, isPopular: false, buttonText: 'Empezar gratis', buttonVariant: 'outline' },
              { id: 2, slug: 'pro', name: 'Pro', description: 'Para el traductor profesional', monthlyPrice: '49', yearlyPrice: '490', yearlyDiscount: 17, benefits: ['25 tokens incluidos, se acumulan cada mes.', 'Adicionales: $1.99/token.', 'Todo lo del Free, más:', 'Certificado de traducción embebido', 'Sello "Human Verified" con QR', 'Manuscritos', 'Hasta 3 corpus de traducción', 'Modo Confidencial (Nuevo)', 'Dashboard de privacidad', 'Retención configurable', 'Historial: 90 días', 'Portal cliente (Pronto)'], creditsMonthly: 25, creditsYearly: 300, isPopular: true, buttonText: 'Empezar con Pro', buttonVariant: 'primary' },
              { id: 3, slug: 'max', name: 'Max', description: 'Para crecer tu operación', monthlyPrice: '149', yearlyPrice: '1490', yearlyDiscount: 17, benefits: ['100 tokens incluidos, se acumulan cada mes.', 'Adicionales: $1.49/token.', 'Todo lo del Pro, más:', 'Hasta 2 miembros (+$39/mes c/u extra)', 'Asistente de post-edición (Nuevo)', 'División de trabajo', 'Corpus ilimitados', 'Verificación con OTP', 'Brackets automáticos', 'Retención hasta 90 días', 'Historial: 1 año'], creditsMonthly: 100, creditsYearly: 1200, isPopular: false, buttonText: 'Empezar con Max', buttonVariant: 'outline' },
              { id: 4, slug: 'team', name: 'Team', description: 'Para oficinas de traducción', monthlyPrice: '499', yearlyPrice: '4990', yearlyDiscount: 17, benefits: ['400 tokens para 2 a 20 miembros. Se acumulan.', 'Adicionales: $1.25/token.', 'Todo lo del Max, más:', 'Panel de administrador', 'Asignación automática por idioma', 'Rol de supervisor', 'Métricas de throughput', 'Auditor de consistencia', 'Reportes exportables', 'Soporte prioritario', 'Historial: 2 años'], creditsMonthly: 400, creditsYearly: 4800, isPopular: false, buttonText: 'Empezar con Team', buttonVariant: 'outline' },
              { id: 5, slug: 'enterprise', name: 'Enterprise', description: 'Infraestructura dedicada', monthlyPrice: '0', yearlyPrice: '0', yearlyDiscount: 0, benefits: ['+20 miembros. Volumen y SLA negociados.', 'Todo lo del Team, más:', 'SSO / SAML', 'API access', 'SLA garantizado', 'Retención custom (hasta 10 años)', 'White-label client portal', 'Audit trail completo', 'Account manager dedicado', 'Facturación NET 30', 'Onboarding asistido'], creditsMonthly: 0, creditsYearly: 0, isPopular: false, buttonText: 'Contactar ventas', buttonVariant: 'outline' },
            ] as Plan[];

            // Filter by audience: individuals get personal plans; teams get
            // team/business/enterprise plans.
            const filtered = allPlans.filter((p) =>
              audience === 'teams' ? isTeamPlan(p) : !isTeamPlan(p),
            );
            const visiblePlans = filtered.length > 0 ? filtered : allPlans;
            // Use setPlanStartIndex to no-op writes so the parent prop stays
            // stable — we no longer expose the left/right chevrons.
            void planStartIndex; void setPlanStartIndex;

            // Responsive grid: keep 3-col max so 4 team plans wrap cleanly.
            const gridCols = visiblePlans.length === 1
              ? 'md:grid-cols-1 md:max-w-md md:mx-auto'
              : visiblePlans.length === 2
                ? 'md:grid-cols-2 md:max-w-3xl md:mx-auto'
                : 'md:grid-cols-3';

            return (
              <div className="relative py-4">
                <div className={`grid ${gridCols} gap-6`}>
                  {/* Dynamic Plans from Database */}
                  {visiblePlans.map((plan, index) => {
                    const price = isYearly ? Math.round(parseFloat(plan.yearlyPrice)) : Math.round(parseFloat(plan.monthlyPrice));
                    const isPopular = plan.isPopular;
                    const isPrimary = plan.buttonVariant === 'primary';
                    const displayCredits = isYearly
                      ? (plan.creditsYearly || 0)
                      : (plan.creditsMonthly || plan.pagesPerMonth || 0);

                    return (
                      <FadeInCard
                        key={plan.id}
                        index={index}
                        className={`relative rounded-2xl ${
                          isPopular ? 'border-2 border-purple-500' : 'border border-gray-200'
                        } bg-white p-6 transition-shadow ${
                          isPopular ? 'hover:shadow-xl' : 'hover:shadow-lg hover:border-gray-300'
                        } flex flex-col h-full`}
                      >
                        {isPopular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <span className="bg-purple-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                              {t.mostPopular}
                            </span>
                          </div>
                        )}
                        <div className="mb-6">
                          <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                        </div>
                        <div className="flex items-baseline gap-2 mb-4">
                          <span className="text-5xl font-bold text-gray-900">${price}</span>
                          {price > 0 && (
                            <>
                              <span className="text-gray-500 text-lg">/{isYearly ? t.year : t.month}</span>
                              {isYearly && plan.yearlyDiscount > 0 && (
                                <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-full">
                                  -{plan.yearlyDiscount}%
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        {/* Credits badge */}
                        {displayCredits > 0 && (
                          <div className="bg-purple-50 rounded-lg px-3 py-2 mb-6">
                            <div className="flex items-center gap-2">
                              <Coins className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-semibold text-purple-700">
                                {/* Fixed locale: bare toLocaleString() renders "1,200" on the
                                    server but "1200" in es-* browsers → hydration mismatch. */}
                                {displayCredits.toLocaleString('en-US')} {t.creditsIncluded}
                              </span>
                              <span className="text-xs text-purple-500">
                                {isYearly ? t.perYear : t.perMonth}
                              </span>
                            </div>
                          </div>
                        )}
                        <ul className="space-y-4 text-sm text-gray-600 flex-1">
                          {plan.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                        <Button
                          asChild
                          variant={isPrimary ? 'default' : 'outline'}
                          className={`w-full h-11 rounded-lg mt-8 ${
                            isPrimary
                              ? 'bg-purple-600 hover:bg-purple-700 text-white'
                              : 'border-gray-200'
                          }`}
                        >
                          <Link href="/sign-up">
                            {plan.buttonText}
                          </Link>
                        </Button>
                      </FadeInCard>
                    );
                  })}
                </div>

                {/* Dots indicator */}
                {allPlans.length > 3 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    {Array.from({ length: allPlans.length - 2 }).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setPlanStartIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                          planStartIndex === idx ? 'bg-purple-600' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

        <AnimationContainer delay={0.3}>
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="flex items-center gap-2 text-gray-600">
              <CreditCard className="w-5 h-5" />
              <span>{t.noCard}</span>
            </div>
          </div>
        </AnimationContainer>
      </div>
    </section>
  );
}
